// @ts-nocheck
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHotelPartnerId } from './useHotelPartnerId';
import { releaseDays } from '../roomBlockUtils';

export function useAssignedEventIds() {
  const { data: partnerId } = useHotelPartnerId();

  const q = useQuery({
    queryKey: ['hotel-report-event-ids', partnerId],
    enabled: !!partnerId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('hotel_event_assignments')
        .select('event_id, events!inner(name, start_date)')
        .eq('hotel_partner_user_id', partnerId);
      if (error) throw error;
      return (data || []).map((d: any) => ({
        event_id: d.event_id as string,
        event_name: (d.events?.name || '') as string,
        event_date: (d.events?.start_date || '') as string,
      }));
    },
  });

  return { assignedEvents: q.data || [], isLoading: q.isLoading, partnerId };
}

/* Report 1 — Compliance */
export function useComplianceReport(partnerId: string | undefined, eventFilter: string) {
  return useQuery({
    queryKey: ['report-compliance', partnerId, eventFilter],
    enabled: !!partnerId,
    queryFn: async () => {
      let q = (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .eq('hotel_partner_user_id', partnerId);
      if (eventFilter !== 'all') q = q.eq('event_id', eventFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

/* Report 2 — Room Night Summary */
export function useRoomNightSummary(eventIds: string[], eventFilter: string) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['report-room-nights', eventIds, eventFilter],
    enabled: eventIds.length > 0 && !!partnerId,
    queryFn: async () => {
      const ids = eventFilter !== 'all' ? [eventFilter] : eventIds;
      const { data, error } = await (supabase as any)
        .from('hotel_bookings')
        .select('event_id, hotel_name, room_nights, number_of_rooms, events!inner(name)')
        .in('event_id', ids)
        .in('status', ['confirmed', 'pending']);
      if (error) throw error;
      return data || [];
    },
  });
}

/* Report 3 — Outreach Effectiveness */
export function useOutreachEffectiveness(eventIds: string[], eventFilter: string) {
  const { data: partnerId } = useHotelPartnerId();
  return useQuery({
    queryKey: ['report-outreach', eventIds, eventFilter],
    enabled: eventIds.length > 0 && !!partnerId,
    queryFn: async () => {
      const ids = eventFilter !== 'all' ? [eventFilter] : eventIds;

      // Get outreach logs
      const { data: logs, error: e1 } = await (supabase as any)
        .from('hotel_outreach_log')
        .select('id, event_id, registration_id, sent_at, events!inner(name)')
        .in('event_id', ids);
      if (e1) throw e1;

      // Get hotel bookings
      const { data: bookings, error: e2 } = await (supabase as any)
        .from('hotel_bookings')
        .select('registration_id, created_at')
        .in('event_id', ids);
      if (e2) throw e2;

      // Build a map: registration_id -> earliest booking date
      const bookingMap = new Map<string, Date>();
      (bookings || []).forEach((b: any) => {
        if (!b.registration_id) return;
        const d = new Date(b.created_at);
        const existing = bookingMap.get(b.registration_id);
        if (!existing || d < existing) bookingMap.set(b.registration_id, d);
      });

      // Group by event
      const eventMap = new Map<string, { name: string; sent: number; converted: number }>();
      (logs || []).forEach((log: any) => {
        const eid = log.event_id;
        if (!eventMap.has(eid)) eventMap.set(eid, { name: (log.events as any)?.name || '', sent: 0, converted: 0 });
        const entry = eventMap.get(eid)!;
        entry.sent++;
        if (log.registration_id) {
          const bookDate = bookingMap.get(log.registration_id);
          if (bookDate) {
            const sentDate = new Date(log.sent_at);
            const diffDays = (bookDate.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24);
            if (diffDays >= 0 && diffDays <= 7) entry.converted++;
          }
        }
      });

      return Array.from(eventMap.entries()).map(([eventId, v]) => ({
        event_id: eventId,
        event_name: v.name,
        emails_sent: v.sent,
        bookings_within_7d: v.converted,
        conversion_rate: v.sent > 0 ? Math.round((v.converted / v.sent) * 100) : 0,
      }));
    },
  });
}

/* Report 4 — Group Rate Expiry Watch */
export function useGroupRateExpiry(partnerId: string | undefined, eventFilter: string) {
  return useQuery({
    queryKey: ['report-rate-expiry', partnerId, eventFilter],
    enabled: !!partnerId,
    queryFn: async () => {
      const now = new Date();
      const in30 = new Date(now);
      in30.setDate(in30.getDate() + 30);

      let q = (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .eq('hotel_partner_user_id', partnerId)
        .not('group_rate_expiry', 'is', null)
        .lte('group_rate_expiry', in30.toISOString().split('T')[0]);
      if (eventFilter !== 'all') q = q.eq('event_id', eventFilter);

      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((d: any) => {
        const expiry = new Date(d.group_rate_expiry);
        const daysRemaining = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return { ...d, days_remaining: daysRemaining };
      });
    },
  });
}

/* Report 5 — Revenue & Commission */
export function useRevenueSummary(partnerId: string | undefined, eventFilter: string) {
  return useQuery({
    queryKey: ['report-revenue', partnerId, eventFilter],
    enabled: !!partnerId,
    queryFn: async () => {
      let q = (supabase as any)
        .from('hotel_revenue_summary')
        .select('*, events!inner(name), hotel_properties(hotel_name)')
        .eq('hotel_partner_user_id', partnerId);
      if (eventFilter !== 'all') q = q.eq('event_id', eventFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });
}

/* Report 6 — Attrition Risk */
export function useAttritionRisk(partnerId: string | undefined, eventIds: string[], eventFilter: string) {
  return useQuery({
    queryKey: ['report-attrition', partnerId, eventIds, eventFilter],
    enabled: !!partnerId && eventIds.length > 0,
    queryFn: async () => {
      const ids = eventFilter !== 'all' ? [eventFilter] : eventIds;

      // Compliance owns booked totals, including room-block reservations.
      const { data: compliance, error: e1 } = await (supabase as any)
        .from('v_hotel_booking_compliance')
        .select('*')
        .eq('hotel_partner_user_id', partnerId)
        .in('event_id', ids);
      if (e1) throw e1;

      // Get hotel properties with rooms_released_date
      const { data: hotels, error: e2 } = await (supabase as any)
        .from('hotel_properties')
        .select('id, event_id, hotel_name, rooms_contracted, rooms_released_date, attrition_pct')
        .in('event_id', ids);
      if (e2) throw e2;

      // The view is event-level. Keep event totals on one row, never repeat
      // them for each property or recompute booked rooms from bookings alone.
      return (compliance || []).flatMap((comp: any) => {
        const properties = (hotels || []).filter((h: any) => h.event_id === comp.event_id);
        const releases = properties.map(h => h.rooms_released_date).filter(Boolean).sort();
        const nextRelease = releases[0] || null;
        const days = releaseDays(nextRelease);
        if (!comp.attrition_risk && !(days !== null && days <= 14)) return [];
        const required = properties.reduce((sum, h) => sum + Math.ceil(Number(h.rooms_contracted || 0) * Number(h.attrition_pct || 0) / 100), 0);
        const contracted = Number(comp.rooms_contracted || 0);
        const booked = Number(comp.rooms_booked || 0);
        return [{ event_name: comp.event_name, hotel_name: properties.map(h => h.hotel_name).join(', '),
          rooms_contracted: contracted, rooms_booked: booked, pickup_pct: Number(comp.pickup_pct || 0),
          attrition_threshold: contracted > 0 ? Math.round(required / contracted * 100) : 0,
          rooms_released_date: nextRelease, release_days: days, rooms_at_risk: Math.max(0, required - booked) }];
      });
    },
  });
}
